import React from "react";
import { Box, Button, Chip, Typography } from "@mui/material";
import { Close, Check, Edit } from "@mui/icons-material";
import moment from "moment";
import { WeeklyWorkDayDto } from "../../../../api-client";

const daysOfWeek = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

export interface PendingChange {
  type: "create" | "update";
  weeklyWorkDay: WeeklyWorkDayDto;
}

interface PendingChangeBannerProps {
  pendingChanges: PendingChange[];
  onCancel: () => void;
  onApply: () => void;
}

/**
 * Banner shown above the calendar when there are pending changes
 * that require versioning. Shows change summary with Cancel/Apply buttons.
 */
const PendingChangeBanner: React.FC<PendingChangeBannerProps> = ({
  pendingChanges,
  onCancel,
  onApply,
}) => {
  if (pendingChanges.length === 0) return null;

  const formatChange = (change: PendingChange) => {
    const day = daysOfWeek[change.weeklyWorkDay.dayOfWeek || 0];
    const start = moment(change.weeklyWorkDay.startTime).format("HH:mm");
    const end = moment(change.weeklyWorkDay.endTime).format("HH:mm");
    const type = change.weeklyWorkDay.isBreak ? "Pause" : "Arbeit";
    return `${day} ${start}-${end} (${type})`;
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        p: 1.5,
        mb: 1,
        bgcolor: "grey.100",
        borderRadius: 1,
        border: "1px solid",
        borderColor: "grey.300",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", flex: 1 }}>
        <Edit fontSize="small" color="action" />
        <Typography variant="body2" fontWeight={600} sx={{ mr: 1 }}>
          {pendingChanges.length === 1
            ? "1 ausstehende Änderung:"
            : `${pendingChanges.length} ausstehende Änderungen:`}
        </Typography>
        {pendingChanges.map((change, index) => (
          <Chip
            key={index}
            label={formatChange(change)}
            size="small"
            color={change.type === "create" ? "success" : "info"}
            variant="outlined"
            sx={{ fontWeight: 500 }}
          />
        ))}
      </Box>
      <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          startIcon={<Close />}
          onClick={onCancel}
        >
          Verwerfen
        </Button>
        <Button
          size="small"
          variant="contained"
          color="primary"
          startIcon={<Check />}
          onClick={onApply}
        >
          Anwenden
        </Button>
      </Box>
    </Box>
  );
};

export default PendingChangeBanner;
